import { useMemo } from "react";

const normalizeDescription = (description) => {
  if (!description) return "";
  if (Array.isArray(description)) return description.filter(Boolean).join("\n");
  if (typeof description === "string") return description;
  return String(description);
};

const splitIntoBlocks = (text) => {
  const normalized = text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .trim();

  if (!normalized) return [];

  return normalized
    .split(/\n\s*\n+/)
    .map((block) => block.trim())
    .filter(Boolean);
};

const splitLines = (block) =>
  block
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const isBulletLine = (line) => /^([•\-*]|\u2022)\s+/.test(line);

const stripBullet = (line) => line.replace(/^([•\-*]|\u2022)\s+/, "");

const isMeaningfulValue = (value) => {
  const v = String(value ?? "").trim();
  if (!v) return false;

  const lower = v.toLowerCase();
  if (["-", "n/a", "na", "null", "undefined"].includes(lower)) return false;

  return true;
};

const isNumericishValueLine = (line) => {
  const t = String(line ?? "").trim();
  if (!t) return false;
  // Examples: 6.9 g, 539 kcal, 826 mg, 10014064000435, ₹199
  return /^([₹$€£]\s*)?[\d,.]+(\s*[a-zA-Z%]+)?$/.test(t);
};

const isProbablyValueLine = (line) => {
  if (!isMeaningfulValue(line)) return false;
  const t = String(line).trim();

  if (isNumericishValueLine(t)) return true;

  // Short, non-sentence values like: Plant-Based, Magic Masala, Savory
  if (t.length <= 32 && !/[.?!]$/.test(t)) {
    if (/\b(per\s+\d|information|features|ingredients|description|disclaimer|highlights)\b/i.test(t)) {
      return false;
    }
    return true;
  }

  return false;
};

const isHeadingLike = (line) => {
  const t = String(line ?? "").trim();
  if (!t) return false;
  if (t.includes(":")) return false;
  if (isBulletLine(t)) return false;
  if (isNumericishValueLine(t)) return false;
  if (t.length > 60) return false;
  return true;
};

const isNutritionalHeading = (line) =>
  /\bnutritional\s+information\b/i.test(String(line ?? "").trim());

const looksLikeStandaloneParagraph = (line) => {
  const t = line.trim();
  if (t.length >= 90) return true;
  return /[.!?]/.test(t);
};

const splitKeyValue = (line) => {
  const idx = line.indexOf(":");
  if (idx <= 0) return null;

  const key = line.slice(0, idx).trim();
  const value = line.slice(idx + 1).trim();

  if (!key || !isMeaningfulValue(value)) return null;
  
  if (key.length > 50) return null;

  return { key, value };
};

const ProductDescription = ({ description }) => {
  // Check if description contains HTML tags
  const isHTML = useMemo(() => {
    if (!description) return false;
    const text = typeof description === 'string' ? description : String(description);
    return /<[^>]+>/.test(text);
  }, [description]);

  const blocks = useMemo(() => {
    const text = normalizeDescription(description);
    return splitIntoBlocks(text);
  }, [description]);

  if (!description) return null;

  // If description contains HTML, render it directly
  if (isHTML) {
    return (
      <div 
        className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    );
  }

  // If no blocks were created but we have a description, render it as simple text
  if (!blocks.length) {
    const simpleText = normalizeDescription(description);
    if (simpleText) {
      return (
        <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
          <p>{simpleText}</p>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
      <div className="space-y-3">
        {blocks.map((block, blockIndex) => {
          const lines = splitLines(block);

          // Common case: backend sends a short, single-line plain-text description.
          // Our heuristics may classify it as a heading and drop it; render it as a paragraph.
          if (lines.length === 1) {
            const onlyLine = lines[0];
            const wordCount = String(onlyLine).trim().split(/\s+/).filter(Boolean).length;

            if (wordCount >= 4 || /[.!?]/.test(onlyLine)) {
              return <p key={blockIndex}>{onlyLine}</p>;
            }
          }

          const bulletLines = lines.filter(isBulletLine);
          const nonBulletLines = lines.filter((l) => !isBulletLine(l));

          if (bulletLines.length && nonBulletLines.length === 0) {
            return (
              <ul key={blockIndex} className="list-disc pl-5 space-y-1">
                {bulletLines.map((line, idx) => (
                  <li key={idx}>{stripBullet(line)}</li>
                ))}
              </ul>
            );
          }

          return (
            <div key={blockIndex} className="space-y-1">
              {(() => {
                const rendered = [];

                for (let i = 0; i < lines.length; i++) {
                  const line = lines[i];
                  const next = lines[i + 1];
                  const nextNext = lines[i + 2];

                  if (isBulletLine(line)) {
                    rendered.push(
                      <div key={i} className="flex gap-2">
                        <span className="mt-[2px]">•</span>
                        <span>{stripBullet(line)}</span>
                      </div>
                    );
                    continue;
                  }

                  const kv = splitKeyValue(line);
                  if (kv) {
                    rendered.push(
                      <div key={i} className="space-y-0.5">
                        <div className="text-gray-600 dark:text-gray-400">
                          {kv.key}:
                        </div>
                        <div className="text-gray-900 dark:text-gray-100">
                          {kv.value}
                        </div>
                      </div>
                    );
                    continue;
                  }

                  // Section headings (including nested like: Info -> Description)
                  if (isHeadingLike(line)) {
                    // Nutritional Information -> render as table
                    if (isNutritionalHeading(line)) {
                      const rows = [];
                      let j = i + 1;

                      while (j < lines.length) {
                        const l = lines[j];
                        const n = lines[j + 1];

                        // Stop the table when we hit paragraph content or a new section.
                        if (looksLikeStandaloneParagraph(l) || isBulletLine(l)) break;

                        if (isHeadingLike(l) && !n) {
                          j++;
                          continue;
                        }

                        const kvInline = splitKeyValue(l);
                        if (kvInline) {
                          rows.push(kvInline);
                          j++;
                          continue;
                        }

                        if (
                          isHeadingLike(l) &&
                          !!n &&
                          !isBulletLine(n) &&
                          !n.includes(":") &&
                          isProbablyValueLine(n)
                        ) {
                          rows.push({ key: l, value: n });
                          j += 2;
                          continue;
                        }

                        // New heading without value -> end table
                        if (isHeadingLike(l) && !!n && isHeadingLike(n)) break;

                        j++;
                      }

                      if (rows.length) {
                        rendered.push(
                          <div
                            key={i}
                            className="pt-2 font-bold text-gray-900 dark:text-gray-100"
                          >
                            {line}
                          </div>
                        );
                        rendered.push(
                          <div
                            key={`${i}-table`}
                            className="mt-1 overflow-hidden rounded-lg border border-black/10 dark:border-white/10"
                          >
                            <table className="w-full text-sm">
                              <tbody className="divide-y divide-black/10 dark:divide-white/10">
                                {rows.map((r, rowIdx) => (
                                  <tr key={rowIdx}>
                                    <td className="py-2 px-3 text-gray-700 dark:text-gray-300 align-top">
                                      {r.key}
                                    </td>
                                    <td className="py-2 px-3 text-gray-900 dark:text-gray-100 text-right whitespace-nowrap align-top">
                                      {r.value}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      }

                      i = j - 1;
                      continue;
                    }

                    // Title + subtitle + paragraph (Info -> Description -> ...)
                    if (isHeadingLike(next) && !!nextNext && looksLikeStandaloneParagraph(nextNext)) {
                      rendered.push(
                        <div
                          key={i}
                          className="pt-2 font-bold text-gray-900 dark:text-gray-100"
                        >
                          {line}
                        </div>
                      );
                      rendered.push(
                        <div key={`${i}-sub`} className="text-gray-700 dark:text-gray-300">
                          {next}
                        </div>
                      );
                      i++;
                      continue;
                    }

                    // Normal heading (only render if it has content after it)
                    if (
                      (!!next && (looksLikeStandaloneParagraph(next) || isBulletLine(next))) ||
                      (!!next && !!nextNext && isProbablyValueLine(nextNext)) ||
                      (!!next && !next.includes(":") && isProbablyValueLine(next))
                    ) {
                      rendered.push(
                        <div
                          key={i}
                          className="pt-2 font-bold text-gray-900 dark:text-gray-100"
                        >
                          {line}
                        </div>
                      );
                    }

                    continue;
                  }

                  // 2-line key/value pairs:
                  //   Type
                  //   Chips & Crisps
                  if (
                    !!next &&
                    !isBulletLine(next) &&
                    !next.includes(":") &&
                    isProbablyValueLine(next)
                  ) {
                    rendered.push(
                      <div key={i} className="space-y-0.5">
                        <div className="text-gray-600 dark:text-gray-400">
                          {line}
                        </div>
                        <div className="text-gray-900 dark:text-gray-100">
                          {next}
                        </div>
                      </div>
                    );
                    i++;
                    continue;
                  }

                  // Keep real paragraph text; otherwise drop title-only noise.
                  if (looksLikeStandaloneParagraph(line)) {
                    rendered.push(<p key={i}>{line}</p>);
                    continue;
                  }
                }

                return rendered;
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductDescription;
